import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnDestroy, output, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Subscription, take } from 'rxjs';
import { briefPostTypeClass, briefPostTypeLabel } from '@/app/common/brief-ask/summarise-brief-ask';
import { SearchBriefResponseDto } from '@/app/common/general-types/briefs.dto';
import { BriefService } from '@/app/services/brief/brief.service';
import { ImageService } from '@/app/services/image/image.service';
import { BriefQuickSettingsComponent } from '@/app/pages/briefs/molecules/brief-quick-settings/brief-quick-settings.component';
import { BriefListItem } from '../../services/briefs-list.types';
import {
  actionButton,
  actionCommands,
  actionText,
  avatarColor,
  avatarInitial,
  creatorsAria,
  creatorsText,
  endsDisplay,
  mergeSearchBrief,
  overviewCommands,
  ownerLabel,
  statusLabel,
  toSearchBrief,
  typeLabel,
  updatedLabel,
} from '../../services/briefs-list.helpers';

export type BriefRowEvent = 'open' | 'setup' | 'duplicate' | 'pause' | 'archive' | 'menu' | 'prefetch';

@Component({
  selector: 'app-brief-row',
  imports: [RouterLink, BriefQuickSettingsComponent],
  templateUrl: './brief-row.html',
  styleUrl: './brief-row.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    tabindex: '0',
    '[class.menu-open]': 'menuOpen()',
    '(click)': 'onOpen($event)',
    '(keydown.enter)': 'onOpen($event)',
    '(mouseenter)': 'rowEvent.emit("prefetch")',
    '(focusin)': 'rowEvent.emit("prefetch")',
  },
})
export class BriefRow implements OnDestroy {
  private readonly briefService = inject(BriefService);
  protected readonly imageService = inject(ImageService);
  private readonly subscriptionList = new Subscription();
  private readonly loadedBrief = signal<SearchBriefResponseDto | null>(null);
  protected readonly requiredFromBrief = signal<number | null>(null);
  private loadedId = '';

  readonly brief = input.required<BriefListItem>();
  readonly meId = input('');
  readonly menuOpen = input(false);
  readonly rowEvent = output<BriefRowEvent>();
  readonly settingsUpdated = output<Partial<SearchBriefResponseDto>>();
  protected readonly settingsBrief = computed(() => {
    const item = this.brief();
    if (item.type !== 'ugc') {
      return null;
    }
    const stub = toSearchBrief(item);
    const loaded = this.loadedBrief();
    if (!loaded || loaded.id !== item.id) {
      return stub;
    }
    return { ...loaded, title: item.name, currentStatus: stub.currentStatus };
  });

  constructor() {
    effect(() => {
      const item = this.brief();
      if (item.type !== 'ugc' || item.id === this.loadedId) {
        return;
      }
      this.loadedId = item.id;
      this.requiredFromBrief.set(null);
      this.subscriptionList.add(
        this.briefService.getBriefById(item.id).pipe(take(1)).subscribe({
          next: (full) => {
            if (this.brief().id !== item.id) {
              return;
            }
            this.loadedBrief.set(mergeSearchBrief(item, full));
            this.requiredFromBrief.set(
              typeof full.requiredCreators === 'number' ? full.requiredCreators : null,
            );
          },
        }),
      );
    });
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  protected readonly typeLabel = typeLabel;
  protected readonly statusLabel = statusLabel;
  protected readonly actionText = actionText;
  protected readonly actionButton = actionButton;
  protected readonly actionCommands = actionCommands;
  protected readonly overviewCommands = overviewCommands;
  protected readonly endsDisplay = endsDisplay;
  protected readonly updatedLabel = updatedLabel;
  protected readonly avatarColor = avatarColor;
  protected readonly avatarInitial = avatarInitial;
  protected readonly creatorsText = creatorsText;
  protected readonly creatorsAria = creatorsAria;
  protected readonly postTypeLabel = briefPostTypeLabel;

  protected postTypePillClass(brief: SearchBriefResponseDto): string {
    return `inline-flex max-w-full items-center truncate rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${briefPostTypeClass(brief)}`;
  }

  /** Full UGC brief, once loaded, so the post-type pill matches brief overview. */
  protected readonly postTypeBrief = computed(() => {
    const item = this.brief();
    if (item.type !== 'ugc') {
      return null;
    }
    const loaded = this.loadedBrief();
    if (!loaded || loaded.id !== item.id) {
      return null;
    }
    return this.settingsBrief();
  });

  protected statusClass(): string {
    const base = 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold';
    switch (this.brief().status) {
      case 'live':
        return `${base} bg-emerald-500/10 text-emerald-800 dark:text-emerald-300`;
      case 'paused':
        return `${base} bg-orange-500/10 text-orange-800 dark:text-orange-300`;
      case 'completed':
        return `${base} bg-zinc-950 text-white dark:bg-zinc-100 dark:text-zinc-950`;
      default:
        return `${base} bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300`;
    }
  }

  protected ownerText(): string {
    const owner = this.brief().owner;
    if (!owner) {
      return 'Unassigned';
    }
    return ownerLabel(owner.name, owner.id, this.meId());
  }

  protected onOpen(event: Event): void {
    if (event.target instanceof Element && event.target.closest('button, a, briefs-brief-quick-settings')) {
      return;
    }
    event.preventDefault();
    this.rowEvent.emit('open');
  }

  protected stop(event: Event): void {
    event.stopPropagation();
  }

  protected emit(kind: BriefRowEvent, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.rowEvent.emit(kind);
  }
}
